import React, { useState } from 'react';
import { Ruler, MessageCircleQuestion, Download } from 'lucide-react';

import EnterpriseSolutionModal from '../components/EnterpriseSolutionModal';
import PageMasthead from '../components/PageMasthead';
import SiteFooter from '../components/SiteFooter';
import SiteHeader from '../components/SiteHeader';

/**
 * CR3ATE SPATIAL MODELS — one of the three products launching at Intergeo.
 *
 * Copy comes from the product one-pager.
 *
 * The sheet's second block — what you send and what comes back — is folded into
 * a single line under the headline rather than kept as its own section. It was
 * the only part that restated the headline at length, and losing it is what lets
 * the page sit inside one screen alongside its call to action.
 */

const OUTCOMES = [
  {
    n: '01',
    label: 'Measure',
    Icon: Ruler,
    body: 'Accurate, georeferenced 3D you can inspect, measure and integrate '
        + 'into existing workflows.',
  },
  {
    n: '02',
    label: 'Analyze',
    Icon: MessageCircleQuestion,
    body: 'Ask questions about objects, conditions, progress, location and '
        + 'change over time.',
  },
  {
    n: '03',
    label: 'Deliver',
    Icon: Download,
    body: 'Generate videos, reports, measurements, extracted assets and '
        + 'immersive experiences.',
  },
] as const;

const Cr3ateSpatialModelsPage: React.FC = () => {
  const [showEnterpriseModal, setShowEnterpriseModal] = useState(false);

  return (
    <div className="flex min-h-dvh flex-col bg-ink-950 text-ink-100">
      <SiteHeader />

      <main className="relative flex-1">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-96 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(12,180,228,0.09),transparent_70%)]"
        />

        <div className="relative mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:py-10">
          {/* The same two-column hero as /desktop: the page's words on the left,
              the offer boxed on the right. The two product pages carry the same
              kind of offer, so they should not put it in two different places. */}
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1.15fr_1fr] lg:items-center lg:gap-12">
            <div>
              <PageMasthead
                title="Cr3ate Spatial Models"
                tagline="Capture reality, create 3D intelligence."
              />
              {/* The pitch, one step down from a display headline — see the note
                  in PageMasthead on why the four pages share a size here. */}
              <p className="mt-6 text-balance font-brand text-xl font-light leading-snug tracking-tight text-ink-100 sm:text-2xl">
                Reality capture that answers you back.
              </p>
              <p className="mt-4 max-w-xl text-balance font-brand text-body font-light leading-relaxed text-ink-300">
                Send images. Get back a Spatial Model — including a Gaussian splat,
                mesh and point cloud, and a URL where you can ask the model
                questions.
              </p>
            </div>

            {/* The one boxed element on the page, and the only place the eye is
                asked to stop — matching /desktop down to the spacing. */}
            <div className="rounded-2xl border border-ember-500/40 bg-ember-500/[0.06] p-6 sm:p-7">
              <p className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
                Intergeo offer
              </p>
              <p className="mt-3 text-balance font-brand text-3xl font-light tracking-tight text-ink-100 sm:text-4xl">
                First Spatial Model free.
              </p>
              <p className="mt-3 text-body font-light text-ink-200">
                Bring your dataset and your question.
              </p>
              <p className="mt-1 text-ui font-light text-ink-400">
                In limited beta — get in touch today!
              </p>
              <button
                type="button"
                onClick={() => setShowEnterpriseModal(true)}
                className="mt-5 inline-flex w-full items-center justify-center rounded-lg bg-crane-500 px-6 py-3 font-brand text-body font-medium text-ink-950 transition-colors hover:bg-crane-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-crane-400 focus-visible:ring-offset-2 focus-visible:ring-offset-ink-950 sm:w-auto"
              >
                Build your first Spatial Model
              </button>
            </div>
          </div>

          <div className="mt-8 border-t border-ink-800 pt-6 lg:mt-10">
            {/* Label only, as on /desktop. A second headline sat here saying
                "One spatial model. Many ways to create value." — which is what
                the label and the three columns below it already say, and it was
                what pushed the page past a 720px-tall window. */}
            <h2 className="font-brand text-meta font-medium uppercase tracking-[0.18em] text-ember-500">
              What your capture becomes
            </h2>

            {/* Three, side by side where there is room: the parallelism is the
                point — one input, three things it turns into. */}
            <div className="mt-6 grid grid-cols-1 gap-x-10 gap-y-7 sm:grid-cols-3">
              {OUTCOMES.map(({ n, label, body, Icon }) => (
                <div key={n}>
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-5 w-5 flex-shrink-0 text-ember-500" aria-hidden />
                    <span className="font-brand text-meta font-medium uppercase tracking-[0.16em] text-ink-400">
                      {n}
                    </span>
                    <span className="font-brand text-title font-medium tracking-wide text-ink-100">
                      {label}
                    </span>
                  </div>
                  <p className="mt-2.5 text-ui font-light leading-relaxed text-ink-300">
                    {body}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-6 text-ui font-light text-ink-400">
              Built on a Gaussian Splatting platform trusted by movie makers and
              surveyors alike.
            </p>
          </div>
        </div>
      </main>

      <SiteFooter />

      <EnterpriseSolutionModal
        isOpen={showEnterpriseModal}
        onClose={() => setShowEnterpriseModal(false)}
      />
    </div>
  );
};

export default Cr3ateSpatialModelsPage;
